<template>
  <div class="p-4">
    <!-- Loading state -->
    <div v-if="loading" class="text-center py-4">
      <div class="shimmerElement h-20 rounded-xl"></div>
    </div>

    <div v-else class="space-y-4">
      <!-- Groupe d'options -->
      <div
        v-for="optionGroup in dishOptions"
        :key="optionGroup.id"
        class="space-y-2"
      >
        <div class="flex items-center justify-between">
          <span class="font-semibold text-sm">
            {{ optionGroup.name }}
            <span v-if="optionGroup.required" class="text-red-500">*</span>
          </span>
          <span v-if="optionGroup.maxChoices > 1" class="text-xs text-gray-500">
            Max {{ optionGroup.maxChoices }}
          </span>
        </div>

        <!-- Choix pour ce groupe -->
        <div class="space-y-2">
          <div
            v-for="choice in optionGroup.choices"
            :key="choice.id"
            @click="toggleChoice(optionGroup, choice)"
            class="flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors"
            :class="{
              'bg-primary-50 border-primary': isChoiceSelected(optionGroup.id, choice.id),
              'bg-white border-gray-200': !isChoiceSelected(optionGroup.id, choice.id)
            }"
          >
            <div class="flex items-center gap-3">
              <!-- Checkbox/Radio indicator -->
              <div
                class="w-5 h-5 rounded border flex items-center justify-center"
                :class="{
                  'bg-primary border-primary': isChoiceSelected(optionGroup.id, choice.id),
                  'border-gray-300': !isChoiceSelected(optionGroup.id, choice.id)
                }"
              >
                <ImgComponent
                  v-if="isChoiceSelected(optionGroup.id, choice.id)"
                  src="/images/all/check.svg"
                  class="w-3 h-3 text-white"
                />
              </div>

              <span class="text-sm">{{ choice.name }}</span>
            </div>

            <!-- Prix du choix -->
            <span v-if="choice.amount > 0" class="text-sm font-semibold">
              +{{ choice.amount }} FCFA
            </span>
          </div>
        </div>
      </div>

      <!-- Validation message -->
      <div v-if="validationError" class="text-red-500 text-sm">
        {{ validationError }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import ImgComponent from '@ui/core/img/ImgComponent.global.vue';

const props = defineProps({
  itemId: {
    type: String,
    required: true
  },
  dishId: {
    type: String,
    required: true
  },
  selectedOptions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update']);

const loading = ref(true);
const dishOptions = ref([]);
const currentSelections = ref([]);
const validationError = ref('');

// Charger les options du plat depuis l'API
onMounted(async () => {
  try {
    const requestId = `fetch-dish-options-${props.dishId}-${Date.now()}`;

    window.bus.emit(window.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      method: 'GET',
      url: `/api/dishes/${props.dishId}`,
      data: {}
    });

    // FIX: Use proper cleanup to prevent memory leaks
    const handler = (res) => {
      if (res.id === requestId) {
        if (res.success) {
          dishOptions.value = res.data?.options || [];
          // Initialiser selections depuis props
          currentSelections.value = props.selectedOptions.map(opt => ({
            optionId: opt.optionId,
            valueId: opt.valueId,
            price: opt.price || 0
          }));
        }
        loading.value = false;
        window.bus.off(window.EVENTS.NETWORK.RESPONSE, handler);
      }
    };

    window.bus.on(window.EVENTS.NETWORK.RESPONSE, handler);
  } catch (error) {
    console.error('Failed to load dish options:', error);
    loading.value = false;
  }
});

const isChoiceSelected = (optionId, valueId) => {
  return currentSelections.value.some(s =>
    s.optionId === optionId && s.valueId === valueId
  );
};

const toggleChoice = (optionGroup, choice) => {
  const isSelected = isChoiceSelected(optionGroup.id, choice.id);

  if (isSelected) {
    // Désélectionner
    if (optionGroup.required) {
      validationError.value = `${optionGroup.name} est requis`;
      return;
    }
    currentSelections.value = currentSelections.value.filter(s =>
      s.optionId !== optionGroup.id || s.valueId !== choice.id
    );
  } else {
    // Sélectionner
    const selectionCount = currentSelections.value.filter(s =>
      s.optionId === optionGroup.id
    ).length;

    if (selectionCount >= optionGroup.maxChoices) {
      validationError.value = `Max ${optionGroup.maxChoices} choix pour ${optionGroup.name}`;
      return;
    }

    currentSelections.value.push({
      optionId: optionGroup.id,
      valueId: choice.id,
      price: choice.amount || 0
    });
  }

  validationError.value = '';
  emit('update', currentSelections.value);
};

onBeforeUnmount(() => {
  // Cleanup is handled by the once() pattern in the handler
});
</script>

<style scoped>
.shimmerElement {
  animation: shimmer 2s infinite;
  background: linear-gradient(
    to right,
    #f3f4f6 0%,
    #e5e7eb 20%,
    #f3f4f6 40%,
    #f3f4f6 100%
  );
  background-size: 1000px 100%;
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}
</style>
